import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAllAlbums, getAlbumStats } from '../../services/albumService';

const ADMIN_SESSION_KEY = 'vf_admin_session';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminAlbums] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Albums</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminAlbums() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [albums, setAlbums] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      console.error('[AdminAlbums] Auth check failed:', err);
      setError(err.message);
      navigate('/admin', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  const fetchAlbums = useCallback(async () => {
    setFetching(true);
    setError(null);
    
    try {
      const albumList = await getAllAlbums();
      setAlbums(albumList);
    } catch (err) {
      console.error('Error fetching albums:', err);
      setError(err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlbums();
    }
  }, [isAuthenticated, fetchAlbums]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    navigate('/admin', { replace: true });
  };

  const viewAlbum = (slug) => {
    window.open(`/v/${slug}`, '_blank');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        {error && (
          <div style={{ background: '#7f1d1d', padding: '1rem', borderBottom: '1px solid #ef4444', textAlign: 'center' }}>
            <p style={{ color: '#fecaca', margin: 0 }}>⚠️ {error}</p>
          </div>
        )}
        
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              </Link>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>Logout</button>
          </div>
        </header>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  color: tab.id === 'albums' ? '#fff' : '#9ca3af',
                  background: tab.id === 'albums' ? '#333' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ margin: 0 }}>Albums</h1>
            <button onClick={fetchAlbums} disabled={fetching} style={{ padding: '0.5rem 1rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: fetching ? 'not-allowed' : 'pointer' }}>
              {fetching ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
              {albums.length} album{albums.length !== 1 ? 's' : ''} found. Albums are stored as JSON files. Metadata shown below is from Firestore.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {fetching ? (
              <p style={{ color: '#666' }}>Loading albums...</p>
            ) : albums.length === 0 ? (
              <p style={{ color: '#666' }}>No albums found</p>
            ) : (
              albums.map(album => (
                <div key={album.slug} style={{ background: '#1a1a1a', borderRadius: 12, overflow: 'hidden', border: '1px solid #333' }}>
                  <div style={{ height: 120, background: album.avatar || album.logo ? `url(${album.avatar || album.logo}) center/cover` : '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {!album.avatar && !album.logo && (
                      <span style={{ fontSize: '2rem', fontWeight: 700, color: '#e50914' }}>{album.name?.charAt(0) || album.slug?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{album.name || album.slug}</h3>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>/v/{album.slug}</p>
                      </div>
                      <span style={{ background: '#333', color: '#fff', padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.7rem' }}>
                        {album.source === 'json' ? 'JSON' : 'Firestore'}
                      </span>
                    </div>
                    
                    {album.subtitle && <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#9ca3af' }}>{album.subtitle}</p>}

                    {album.stats && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem', padding: '0.75rem', background: '#222', borderRadius: 6 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>{album.stats.photos || 0}</div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>Photos</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>{album.stats.videos || 0}</div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>Videos</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 600 }}>{album.stats.views || 0}</div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>Views</div>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => viewAlbum(album.slug)} style={{ flex: 1, padding: '0.5rem', background: '#e50914', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
                        View Album
                      </button>
                      <button onClick={() => setSelectedAlbum(album)} style={{ padding: '0.5rem 0.75rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
                        Manage
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {selectedAlbum && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '2rem', maxWidth: 600, width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
                <h2 style={{ marginTop: 0 }}>Manage: {selectedAlbum.name || selectedAlbum.slug}</h2>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '0.5rem' }}>Album URL</h3>
                  <input value={`${window.location.origin}/v/${selectedAlbum.slug}`} readOnly style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', fontFamily: 'monospace' }} />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '0.5rem' }}>Access ({selectedAlbum.access?.length || 0} users)</h3>
                  {selectedAlbum.access?.length > 0 ? (
                    <ul style={{ paddingLeft: '1.5rem', color: '#9ca3af', fontSize: '0.85rem' }}>
                      {selectedAlbum.access.map((a, i) => (
                        <li key={i} style={{ marginBottom: '0.25rem' }}>{a.userUid} - {a.role}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ color: '#666', fontSize: '0.85rem' }}>No access configured</p>
                  )}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '0.9rem', color: '#9ca3af', marginBottom: '0.5rem' }}>Statistics</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                    <div style={{ padding: '0.75rem', background: '#222', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{selectedAlbum.stats?.categories || 0}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>Categories</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: '#222', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{selectedAlbum.stats?.photos || 0}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>Photos</div>
                    </div>
                    <div style={{ padding: '0.75rem', background: '#222', borderRadius: 6, textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{selectedAlbum.stats?.videos || 0}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>Videos</div>
                    </div>
                  </div>
                </div>

                <button onClick={() => setSelectedAlbum(null)} style={{ width: '100%', padding: '0.75rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          )}
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminAlbums;